import { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { Line } from 'react-chartjs-2';
import { styled } from '@mui/material/styles';
import {
  UsageTypeSwitch,
  UsageType,
  UsageTypeValue,
} from 'src/common/components/UsageTypeSwitch/UsageTypeSwitch';
import { GraphsColorLegend } from 'src/common/components/GraphColorsLegend/GraphColorsLegend';
import { ChartType } from 'src/common/components/ChartTypeToggle/ChartTypeToggle';
import { useTariffSummaryUsageData } from './hooks/useTariffSummaryUsageData';
import { AreaChart } from 'src/common/components/Graphs/common/Graphs/AreaChart';
import { parseResponseDateAndTime } from 'common/utils/parseResponseDate';
import { WidgetDailyUsageIndicator } from 'src/dashboard/Componets/WidgetDailyUpdateIndicator';
import { EmptyLineOrBarChart } from 'src/common/components/Graphs/common/Graphs/EmptyLineOrBarChart';
import { AreaChartErrorBoundary } from 'src/dashboard/Componets/AreaErrorBoundary';
import { UsageWidget, UsageWidgetLegend } from 'src/dashboard/Componets/UsageWidget';
import { usageTooltipValueFormatter } from 'src/common/utils/mapUsageDTOtoAreaChartData';

const usageChartTooltipLabelOrder = {
  Allowance: 0,
  Total: 1,
  Upload: 2,
  Download: 3,
  MO: 2,
  MT: 3,
};

const StyledChartContainer = styled(Box)(() => ({
  height: '100%',
}));

type UsageChartWrapper = {
  accountRef: string | null;
  tariffCode: string | null;
};

type UsageChartWidgetProps = {
  accountRef: string;
  tariffCode: string;
};

type TariffSummaryUsageChartProps = UsageChartWidgetProps & {
  usageType: UsageTypeValue;
};

const TariffSummaryUsageChart = ({
  tariffCode,
  accountRef,
  usageType,
}: TariffSummaryUsageChartProps) => {
  const tariffUsageData = useTariffSummaryUsageData({
    accountRef,
    tariffCode,
    chartType: ChartType.Cumulative,
    usageType,
  });

  return (
    <>
      <UsageWidgetLegend>
        <GraphsColorLegend legendItems={tariffUsageData.legend} />
        <WidgetDailyUsageIndicator date={parseResponseDateAndTime(tariffUsageData?.lastUpdated)} />
      </UsageWidgetLegend>
      <AreaChart
        limitLabel="Allowance"
        datasets={tariffUsageData.datasets}
        limit={tariffUsageData.limit}
        yAxisLabel={tariffUsageData.yAxisLabel}
        xAxisLabels={tariffUsageData.xAxisLabels}
        realDataEndIndex={tariffUsageData.realDataEndIndex}
        tooltipValueFormatter={(value: number, unit?: string) =>
          usageTooltipValueFormatter(value, usageType, unit)
        }
        tooltipLabelPriority={usageChartTooltipLabelOrder}
      />
    </>
  );
};

const UsageChartWidgetContent = ({ accountRef, tariffCode }: UsageChartWidgetProps) => {
  const { t } = useTranslation();

  const [usageType, setUsageType] = useState<UsageTypeValue>(UsageType.Data);

  return (
    <UsageWidget
      title={t('dashboard.cumulativeUsageChart.title')}
      headerContent={
        <Box sx={{ marginRight: 'auto' }}>
          <UsageTypeSwitch value={usageType} onChange={setUsageType} />
        </Box>
      }
    >
      <StyledChartContainer role="figure" aria-label="Usage">
        <AreaChartErrorBoundary key={`${accountRef} ${tariffCode}  ${usageType}`}>
          <TariffSummaryUsageChart
            accountRef={accountRef}
            tariffCode={tariffCode}
            usageType={usageType}
          />
        </AreaChartErrorBoundary>
      </StyledChartContainer>
    </UsageWidget>
  );
};

const getEmptyUsageChartWidgetPlaceholderText = (
  {
    accountRef,
    tariffCode,
  }: {
    accountRef: string | null;
    tariffCode: string | null;
  },
  t: TFunction,
) => {
  if (!accountRef) {
    return t('dashboard.placeholders.selectAccount');
  }

  return tariffCode ? '' : t('dashboard.placeholders.selectTariff');
};

export const UsageChartWidget = ({ accountRef, tariffCode }: UsageChartWrapper) => {
  const { t } = useTranslation();

  if (!accountRef || !tariffCode) {
    const emptyChartText = getEmptyUsageChartWidgetPlaceholderText({ accountRef, tariffCode }, t);

    return (
      <UsageWidget title={t('dashboard.cumulativeUsageChart.title')}>
        <StyledChartContainer>
          <EmptyLineOrBarChart as={Line} emptyChartText={emptyChartText} />
        </StyledChartContainer>
      </UsageWidget>
    );
  }

  return <UsageChartWidgetContent accountRef={accountRef} tariffCode={tariffCode} />;
};
